/**
 * print-proposal
 *
 * Submission-ready, high-fidelity PDF export.
 *
 * Renders the SAME ExportableProposalView used in the viewer, then runs it
 * through Paged.js (CSS Paged Media polyfill) so the output gets real page
 * furniture the browser's native print engine cannot produce:
 *   - a furniture-free cover page,
 *   - a running header (company name | solicitation number),
 *   - a running footer (document title · section name · "Page X of Y").
 * The section name is driven by a CSS named string set from each section's <h1>;
 * page numbers use counter(page)/counter(pages). Both require a Paged Media
 * engine — hence Paged.js rather than raw window.print().
 *
 * The whole export is forced into a clean light "Formal-Federal" theme (injected
 * via buildExportCss) so it never inherits the app's dark mode.
 *
 * If Paged.js is unavailable or fails, we fall back to printing the view
 * directly so an export always succeeds (without running headers/footers).
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import type { Contract } from '../types';
import { ExportableProposalView } from '../components/proposal/ExportableProposalView';
import { buildExportCss } from './export/exportStyles';
import { buildCompanyIdentity, type ExportCompanyIdentity } from './export/companyIdentity';
import { resolveSolicitationNumber } from './export/coverPageModel';
import { cleanTitle } from './export/textCleanup';

interface PrintProposalProps {
  proposal: any;
  contract: Contract;
  selectedSections: string[];
  showCitations?: boolean;
  separateCoverLetter?: boolean;
  identity?: ExportCompanyIdentity;
}

const PORTAL_ID = 'proposal-print-root';
const SOURCE_ID = 'proposal-print-source';
const STYLE_ATTR = 'data-proposal-print';

function parseCoverLetter(content: any): any {
  if (!content) return null;
  if (typeof content === 'object') return content;
  try { return JSON.parse(content); } catch { return null; }
}

/** Wait for every <img> in a subtree to finish loading (so the logo paginates). */
async function waitForImages(root: HTMLElement, timeoutMs = 4000): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  if (imgs.length === 0) return;
  await Promise.race([
    Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) return resolve();
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      )
    ),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

export async function printProposalToPdf(props: PrintProposalProps): Promise<void> {
  // Clean up any leftovers from a previous run.
  document.getElementById(PORTAL_ID)?.remove();
  document.getElementById(SOURCE_ID)?.remove();
  document.querySelectorAll(`style[${STYLE_ATTR}]`).forEach((el) => el.remove());

  const coverLetter = parseCoverLetter(props.proposal?.coverLetter);
  const identity = props.identity ?? buildCompanyIdentity(null, coverLetter);

  // Off-canvas source: rendered in the live document so Tailwind/fonts apply.
  const source = document.createElement('div');
  source.id = SOURCE_ID;
  source.style.cssText = 'position:fixed;left:-10000px;top:0;width:816px;';
  document.body.appendChild(source);

  const root: Root = createRoot(source);
  await new Promise<void>((resolve) => {
    root.render(
      <ExportableProposalView
        proposal={props.proposal}
        contract={props.contract}
        selectedSections={props.selectedSections}
        showCitations={props.showCitations ?? false}
        separateCoverLetter={props.separateCoverLetter ?? false}
        identity={identity}
      />
    );
    setTimeout(resolve, 250); // let React commit + layout settle
  });

  try {
    // @ts-ignore document.fonts is widely supported
    if (document.fonts?.ready) await document.fonts.ready;
  } catch { /* non-fatal */ }
  await waitForImages(source);

  // Export CSS (theme + Formal-Federal typography + @page furniture).
  const css = buildExportCss({
    companyName: identity.companyName,
    solicitation: resolveSolicitationNumber(props.contract, coverLetter),
    documentTitle: cleanTitle(props.contract?.title || ''),
  });
  const cssBlobUrl = URL.createObjectURL(new Blob([css], { type: 'text/css' }));

  // Print isolation: hide everything except the paginated portal.
  const styleEl = document.createElement('style');
  styleEl.setAttribute(STYLE_ATTR, 'true');
  styleEl.textContent = `
    @media screen { #${PORTAL_ID} { position: fixed; left: -10000px; top: 0; } }
    @media print {
      body * { visibility: hidden !important; }
      #${PORTAL_ID}, #${PORTAL_ID} * { visibility: visible !important; }
      #${PORTAL_ID} { position: absolute !important; left: 0 !important; top: 0 !important; background:#fff !important; }
      #${PORTAL_ID} * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      html, body { background:#fff !important; }
    }
  `;
  document.head.appendChild(styleEl);

  const portal = document.createElement('div');
  portal.id = PORTAL_ID;
  document.body.appendChild(portal);

  // Captured before Paged.js runs so a corrupted pagination can be detected by
  // comparing surviving text volume against the untouched source.
  const sourceTextLen = (source.innerText || '').replace(/\s+/g, '').length;
  // Some Paged.js/Chromium combinations (observed on Chromium 149) split text
  // nodes incorrectly during pagination and silently drop large chunks of every
  // paragraph — the page count and layout look fine, but real proposal content
  // is missing. A working paginated doc is never shorter than the source (the
  // running header/footer adds text on every page); a meaningful shortfall
  // means content was lost, not just naturally reflowed.
  const PAGED_INTEGRITY_RATIO = 0.85;

  let usedPagedjs = false;
  try {
    const { Previewer } = await import('pagedjs');
    const previewer = new Previewer();
    // Pass the source HTML (documented form) so `source` stays intact for the
    // fallback if Paged.js produces nothing.
    await previewer.preview(source.innerHTML, [cssBlobUrl], portal);
    // Guard against the "two blank pages" failure mode: if Paged.js laid out
    // pages but flowed no content, treat it as a failure and fall back.
    const paged = portal.querySelector('.pagedjs_page');
    const pagedTextLen = (portal.innerText || '').replace(/\s+/g, '').length;
    if (!paged || pagedTextLen <= 50) throw new Error('Paged.js produced empty output');
    if (sourceTextLen > 200 && pagedTextLen < sourceTextLen * PAGED_INTEGRITY_RATIO) {
      throw new Error(
        `Paged.js dropped content during pagination (${pagedTextLen}/${sourceTextLen} characters survived)`
      );
    }
    usedPagedjs = true;
    source.style.display = 'none';
  } catch (err) {
    // Fallback: print the rendered view directly (no @page running furniture,
    // but a complete, correctly-themed document — never blank, never missing
    // content).
    console.error('Paged.js unavailable/corrupted; using direct-print fallback', err);
    portal.innerHTML = '';
    const fallbackStyle = document.createElement('style');
    fallbackStyle.setAttribute(STYLE_ATTR, 'true');
    fallbackStyle.textContent = css;
    document.head.appendChild(fallbackStyle);
    // Source is intact (we previewed from innerHTML) — move it into the portal.
    while (source.firstChild) portal.appendChild(source.firstChild);
    source.remove();
  }

  try {
    // @ts-ignore
    if (document.fonts?.ready) await document.fonts.ready;
  } catch { /* non-fatal */ }
  await waitForImages(portal);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try { root.unmount(); } catch { /* ignore */ }
    document.getElementById(SOURCE_ID)?.remove();
    portal.remove();
    styleEl.remove();
    document.querySelectorAll(`style[${STYLE_ATTR}]`).forEach((el) => el.remove());
    try { URL.revokeObjectURL(cssBlobUrl); } catch { /* ignore */ }
    window.removeEventListener('afterprint', cleanup);
  };

  void usedPagedjs;
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Fallback cleanup if afterprint never fires.
  setTimeout(cleanup, 120000);
}
